import type { ChatToolCall } from '../api/domains/chatbot.api'

/**
 * Assistant tools that change a capacity run on the server. When a finished turn carries one, the
 * /capacity pages re-read: `apps/console` has no query cache, so this module-level signal stands in
 * for it (the same shape as security-refresh.ts).
 */
export const CAPACITY_WRITE_TOOLS: ReadonlySet<string> = new Set(['cancel_capacity_run'])

export interface CapacityChange {
  runIds: string[]
}

type Listener = (change: CapacityChange) => void

const listeners = new Set<Listener>()

export function subscribeCapacityRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/**
 * A cancel that succeeded changed the run. So did one that came back `unknown_outcome`: the request
 * may well have landed (the run stops at its next step), so the pages re-read rather than keep
 * showing a run that has since stopped.
 */
export function announceCapacityWrites(toolCalls: readonly ChatToolCall[]): void {
  const runIds: string[] = []

  for (const call of toolCalls) {
    if (!CAPACITY_WRITE_TOOLS.has(call.name)) continue
    const r = call.result as { success?: unknown; error?: unknown; run_id?: unknown } | null

    if (!r || typeof r !== 'object') continue
    if (typeof r.run_id !== 'string' || !r.run_id) continue
    if (r.success === true || r.error === 'unknown_outcome') runIds.push(r.run_id)
  }

  if (runIds.length === 0) return

  for (const listener of [...listeners]) {
    try {
      listener({ runIds })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}
